import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { useUserOptions, type UserOptionType } from '@/api/user';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxStatus,
  ComboboxValue,
  InfiniteComboboxList,
  useDebouncedSearch,
  type ComboboxProps,
} from '@/components/Form/pickers';
import { useMe } from '@/providers/User';

const PAGE_SIZE = 50;
const ownerChipSizeClasses = {
  small: 'h-[calc(--spacing(4.25))]',
  medium: 'h-[calc(--spacing(6))]',
  large: 'h-[calc(--spacing(7))]',
};

type Props = Omit<
  ComboboxProps<UserOptionType, true>,
  'items' | 'multiple' | 'value' | 'onValueChange' | 'itemToStringValue' | 'itemToStringLabel'
> & {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  value?: UserOptionType[];
  isLoading?: boolean;
  disableOwner?: boolean;
};

const UserMultiSelect = ({
  value,
  isLoading: isLoadingProp,
  className,
  disabled = false,
  disableOwner = false,
  size = 'large',
  placeholder = t`Select Users...`,
  ...rest
}: Props) => {
  const currentUser = useMe();

  const { search, setSearch, onInputValueChange, cancel } = useDebouncedSearch();

  const { userOptions, hasMore, isLoading, isFetchingNextPage, fetchNextPage } = useUserOptions({
    pageSize: PAGE_SIZE,
    search,
    disableOwner,
  });

  const recipientsValue = useMemo(
    () =>
      disableOwner && currentUser
        ? (value ?? []).filter((user) => user.id !== currentUser.id)
        : (value ?? []),
    [disableOwner, currentUser, value],
  );

  const handleOpenChange: ComboboxProps<UserOptionType, true>['onOpenChange'] = (open) => {
    if (open) {
      cancel();
      setSearch('');
      return;
    }

    cancel();
  };

  return (
    <Combobox
      {...rest}
      data-testid="user-multiselect"
      multiple
      filteredItems={userOptions}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoadingProp || isLoading || isFetchingNextPage}
      disabled={disabled}
      size={size}
      value={recipientsValue ?? null}
      onInputValueChange={(nextSearch) => onInputValueChange(nextSearch)}
      onOpenChange={handleOpenChange}
    >
      <ComboboxChips className={className}>
        {disableOwner && currentUser && (
          <span
            key={currentUser.id}
            aria-disabled={disabled}
            className={[
              'inline-flex max-w-full min-w-0 items-center gap-1 rounded-xs px-1.5 text-xs font-medium whitespace-nowrap',
              'bg-green-300 text-gray-900',
              'aria-disabled:cursor-not-allowed aria-disabled:bg-gray-200! aria-disabled:text-gray-500!',
              ownerChipSizeClasses[size],
            ].join(' ')}
          >
            <span className="min-w-0 flex-1 truncate">{currentUser.name}</span>
          </span>
        )}
        <ComboboxValue>
          {recipientsValue.map((item) => (
            <ComboboxChip key={item.id} showRemove={item.id !== currentUser?.id}>
              {item.name}
            </ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput placeholder={placeholder} />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>
          <Trans>No users were found...</Trans>
        </ComboboxEmpty>
        <InfiniteComboboxList
          fetchNextPage={fetchNextPage}
          hasMore={hasMore}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
        >
          {(item: UserOptionType) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {item.name}
            </ComboboxItem>
          )}
        </InfiniteComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default UserMultiSelect;
